const GETAPP_BASE_URL = 'https://www.getapp.com'

export const getFAQStructuredData = (QAMap = {}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: Object.keys(QAMap).map(question => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: QAMap[question],
      },
    })),
  }
  return JSON.stringify(structuredData)
}

/**
 * @param {Array<{ name: string; path: string}>} breadcrumbItemsSection
 */
export const getBreadCrumbStructuredData = breadcrumbItemsSection => {
  const breadcrumbItems = [{ path: '/', name: 'Home' }, ...breadcrumbItemsSection]
  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map(({ name, path }, index) => {
      const position = index + 1
      return {
        '@type': 'ListItem',
        position,
        ...(name ? { name } : {}),
        ...(path && position < breadcrumbItems.length ? { item: `${GETAPP_BASE_URL}${path}` } : {}),
      }
    }),
  }

  return JSON.stringify(breadcrumbStructuredData)
}

export const getVideoStructuredData = video =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.description || video.title,
    thumbnailUrl: `https://i.ytimg.com/vi/${video.videoId}/default.jpg`,
    uploadDate: video.published,
    duration: `PT${Math.floor(video.video_length / 60)
      .toString()
      .padStart(2, '0')}M${(video.video_length % 60).toString().padStart(2, '0')}S`,
    embedUrl: `https://www.youtube.com/embed/${video.videoId}`,
  })

export const HOME_VIDEO_LD = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'GetApp | Where business leaders find software',
  description:
    'Researching, comparing, and choosing the right software for your business can be tricky and time-consuming, especially with so many demands on your time. Forget searching the internet to find authentic or helpful software reviews and recommendations.   Instead, use GetApp.   GetApp hosts a library of over one million verified software reviews from other business owners so you can: – Discover software that meets your needs – Review software features – Compare software reviews  GetApp breaks past the sales pitch to the business-critical information about which apps will work best for your business.  Your software search ends here. Get personalized business software recommendations by visiting GetApp.com—where business leaders find software. https://www.getapp.com/',
  thumbnailUrl: 'https://i.ytimg.com/vi/BCFrrTL_DC8/default.jpg',
  uploadDate: '2021-12-08T16:18:35Z',
  embedUrl: 'https://www.youtube.com/embed/BCFrrTL_DC8',
})

export const ORGANIZATION_LD = JSON.stringify({
  '@context': 'http://schema.org/',
  '@type': 'Organization',
  parentOrganization: 'G2.com, Inc.',
  name: 'GetApp',
  legalName: 'GetApp',
  description:
    'Discover the best business software and check over 2.5 million+ verified ratings & reviews on GetApp. Get data-driven recommendations and compare alternatives to find the right tools for your team.',
  logo: 'https://www.getapp.com/i/getapp-logo-default.svg',
  url: 'https://www.getapp.com',
  email: 'info@getapp.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '100 S. Wacker Dr., Ste. 600',
    addressLocality: 'Chicago',
    addressRegion: 'IL',
    postalCode: '60606',
    addressCountry: 'US',
  },
  sameAs: [
    'https://www.facebook.com/GetAppcom',
    'https://www.instagram.com/getappcom',
    'https://www.linkedin.com/company/getapp-com/posts/',
    'https://x.com/getapp',
    'https://www.youtube.com/c/GetAppCom',
  ],
})

export const WEBSITE_LD = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'GetApp',
  alternateName: 'GetApp',
  url: 'https://www.getapp.com/',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://www.getapp.com/search?query={query}',
    },
    'query-input': 'required name=query',
  },
})

export const getReviewsAiSummaryStructuredData = listing => {
  const reviewsSummary = listing.reviews_summary
  const topics = reviewsSummary.topics || []
  const pros = topics.filter(t => t.type === 'PRO').flatMap(t => t.snippets)
  const cons = topics.filter(t => t.type === 'CON').flatMap(t => t.snippets)

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: listing.name,
    review: {
      '@type': 'Review',
      name: `What do users say about ${listing.name}?`,
    },
    positiveNotes: {
      '@type': 'ItemList',
      itemListElement: pros.map((topic, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: topic.text,
          author: {
            '@type': 'Person',
            name: topic.user_name,
          },
        }
      }),
    },
    negativeNotes: {
      '@type': 'ItemList',
      itemListElement: cons.map((topic, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: topic.text,
          author: {
            '@type': 'Person',
            name: topic.user_name,
          },
        }
      }),
    },
  }

  return JSON.stringify({
    ...schema,
  })
}

export const getSoftwareApplicationLD = listing => {
  const hasReviewsSummary = !!listing.reviews_summary

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: listing.name,
    applicationCategory: 'BusinessApplication',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: listing.rating_avg,
      ratingCount: listing.num_reviews,
    },
  }

  if (hasReviewsSummary) {
    const reviewsSummary = listing.reviews_summary
    const pros = reviewsSummary.topics.filter(t => t.type === 'PRO').flatMap(t => t.snippets) || []
    const cons = reviewsSummary.topics.filter(t => t.type === 'CON').flatMap(t => t.snippets) || []

    schema.positiveNotes = {
      '@type': 'ItemList',
      itemListElement: pros.map((topic, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: topic.text,
          author: {
            '@type': 'Person',
            name: topic.user_name,
          },
        }
      }),
    }
    schema.negativeNotes = {
      '@type': 'ItemList',
      itemListElement: cons.map((topic, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: topic.text,
          author: {
            '@type': 'Person',
            name: topic.user_name,
          },
        }
      }),
    }
  }

  return JSON.stringify({
    ...schema,
  })
}

export const revealSEOUrl = url => url && url.replaceAll('|', '/')

const NUM_REVIEWS_LINK_THRESHOLD = 22
export const hasReviewsPage = numReviews => numReviews > NUM_REVIEWS_LINK_THRESHOLD

/**
 * Removes query parameters that partially match any substring in paramsToRemove.
 * @param {string} url - The original URL (can be relative or absolute).
 * @param {string[]} paramsToRemove - Array of substrings to remove from URL.
 * @returns {string} - The cleaned URL.
 */
export const removeQueryParams = (url, paramsToRemove) => {
  // Early returns for common cases
  if (!url.includes('?') || !paramsToRemove.length) return url

  const [baseUrl, queryString] = url.split('?')
  const searchParams = new URLSearchParams(queryString)

  // Fast path: if no search params, return baseUrl
  if (!searchParams.toString()) return baseUrl

  // Get all keys once outside the loop
  const paramKeys = Array.from(searchParams.keys())
  if (paramKeys.length === 0) return baseUrl

  let changed = false
  const keysToRemove = []

  // Identify all keys that need to be removed (single approach)
  for (let i = 0; i < paramKeys.length; i++) {
    const key = paramKeys[i]

    for (let j = 0; j < paramsToRemove.length; j++) {
      if (key.includes(paramsToRemove[j])) {
        keysToRemove.push(key)
        break // No need to check other params
      }
    }
  }

  // Then remove all identified keys in batch
  if (keysToRemove.length > 0) {
    changed = true
    for (let i = 0; i < keysToRemove.length; i++) {
      searchParams.delete(keysToRemove[i])
    }
  }

  if (!changed) return url

  const newQuery = searchParams.toString()
  return newQuery ? `${baseUrl}?${newQuery}` : baseUrl
}
